import { forwardRef, useEffect, useId, useImperativeHandle, useRef } from "react";
import { cn } from "../utils/cn";

// ── Icons ─────────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg
      width="10" height="10" viewBox="0 0 24 24"
      fill="none" stroke="currentColor"
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function DashIcon() {
  return (
    <svg
      width="10" height="10" viewBox="0 0 24 24"
      fill="none" stroke="currentColor"
      strokeWidth="3.5" strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
    </svg>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CheckboxProps {
  /** Primary label displayed next to the box */
  label: string;
  /** Secondary explanatory text rendered below the label */
  description?: string;
  /** Whether the checkbox is checked */
  checked: boolean;
  /** Called with the new checked state on interaction */
  onChange: (checked: boolean) => void;
  /** Shows a dash instead of a check — used for "select all" parent state */
  indeterminate?: boolean;
  /** Prevents interaction */
  disabled?: boolean;
  /** Validation error — shown below and turns the box border red */
  error?: string;
  /** Additional className on the root label element */
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      checked,
      onChange,
      indeterminate = false,
      disabled = false,
      error,
      className,
    },
    ref,
  ) => {
    const id = useId();
    const internalRef = useRef<HTMLInputElement>(null);

    // Expose the native input through the forwarded ref
    useImperativeHandle(ref, () => internalRef.current!, []);

    // indeterminate is only settable via JS on the DOM element
    useEffect(() => {
      if (internalRef.current) {
        internalRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const isActive = checked || indeterminate;
    const hasError = !!error;

    return (
      <div className={cn("flex flex-col gap-1", className)}>
        <label
          htmlFor={id}
          className={cn(
            "inline-flex items-start gap-2.5 group",
            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          )}
        >
          {/* Native input — screen-reader accessible, visually hidden */}
          <div className="relative flex shrink-0 mt-px">
            <input
              ref={internalRef}
              id={id}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              aria-invalid={hasError}
              aria-describedby={
                error ? `${id}-error` : description ? `${id}-desc` : undefined
              }
              onChange={e => onChange(e.target.checked)}
              className="sr-only peer"
            />

            {/* Custom visual box */}
            <div
              aria-hidden="true"
              className={cn(
                "w-4 h-4 rounded flex items-center justify-center",
                "border-2 transition-all duration-150",
                // Focus ring reflected from the sr-only native input
                "peer-focus-visible:ring-2 peer-focus-visible:ring-brand-purple",
                "peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface",
                isActive
                  ? "bg-brand-purple border-brand-purple"
                  : hasError
                    ? "bg-surface dark:bg-surface-raised border-red-400 dark:border-red-500"
                    : [
                        "bg-surface dark:bg-surface-raised",
                        "border-border-strong dark:border-border-strong",
                        "group-hover:border-brand-purple dark:group-hover:border-violet-500",
                      ].join(" "),
              )}
            >
              {isActive && (
                <span className="text-white animate-scale-in">
                  {indeterminate ? <DashIcon /> : <CheckIcon />}
                </span>
              )}
            </div>
          </div>

          {/* Label + description */}
          <div className="flex flex-col gap-0.5 leading-tight">
            <span className="text-base font-medium text-content-primary">
              {label}
            </span>
            {description && (
              <span
                id={`${id}-desc`}
                className="text-sm text-content-secondary"
              >
                {description}
              </span>
            )}
          </div>
        </label>

        {/* Error message */}
        {error && (
          <p
            id={`${id}-error`}
            role="alert"
            className="text-xs leading-snug text-red-600 dark:text-red-400 pl-6"
          >
            {error}
          </p>
        )}
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";
